import { useMemo } from 'react'

import { PermissionsProps } from './types'
import type { RootState } from 'app/store'
import useUserTopDomainsWithNesting from './useUserTopDomainsWithNesting'
import { useAppSelector } from 'app/hooks'
import {
  domainIsRootSelector,
  domainNestingEnabledSelector,
  isDomainAdminSelector,
  isDomainAdminSomewhereSelector,
  isNonDirectDealerSelector,
  isRootAdminSelector,
} from './selectors'
import {
  DOMAIN_ADMIN,
  DOMAIN_ADMIN_SOMEWHERE,
  DOMAIN_NESTING_ENABLED,
  DOMAINS_DISTRIBUTOR,
  NON_DIRECT_DEALER,
  ROOT_ADMIN,
  ROOT_USER,
} from './consts'
import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'

function usePermissions({ allow, domainId }: PermissionsProps) {
  useUserTopDomainsWithNesting({ domainId })

  const currentRootDomainId = useAppSelector(currentRootDomainIdSelector)

  const isRootAdmin = useAppSelector(isRootAdminSelector)
  const isDomainAdmin = useAppSelector((state: RootState) =>
    isDomainAdminSelector(state, domainId)
  )
  const isDomainAdminSomewhere = useAppSelector(isDomainAdminSomewhereSelector)
  const domainIsRoot = useAppSelector(domainIsRootSelector)
  const domainNestingEnabled = useAppSelector(domainNestingEnabledSelector)
  const isNonDirectDealer = useAppSelector(
    isNonDirectDealerSelector(currentRootDomainId)
  )

  const permissionsMap = useMemo(
    () => ({
      [ROOT_ADMIN]: isRootAdmin,
      [DOMAIN_ADMIN_SOMEWHERE]: isRootAdmin || isDomainAdminSomewhere,
      [DOMAIN_ADMIN]: (isRootAdmin || isDomainAdmin) && !!domainId,
      [ROOT_USER]: domainIsRoot,
      [DOMAIN_NESTING_ENABLED]: domainNestingEnabled,
      [NON_DIRECT_DEALER]: isNonDirectDealer,

      // TODO: add implementation for DISTRIBUTOR and NON_DIRECT_DEALER
      [DOMAINS_DISTRIBUTOR]: false,
    }),
    [
      isRootAdmin,
      isDomainAdminSomewhere,
      isDomainAdmin,
      domainId,
      domainIsRoot,
      domainNestingEnabled,
      isNonDirectDealer,
    ]
  )

  const grantAccess = useMemo(
    () =>
      allow
        .map((permission) => permissionsMap[permission])
        .every((permission) => permission === true),
    [allow, permissionsMap]
  )

  return [grantAccess]
}

export default usePermissions
